import { useEffect, useState, useCallback } from "react";
import {
  Users, Activity, Type, LayoutDashboard, Loader2, Search, ArrowLeft, RefreshCw,
  Circle, MapPin, Monitor, Clock, ChevronLeft, ChevronRight, Shield,
} from "lucide-react";
import { api } from "@/lib/api";

const fmtDate = (s) => (s && !isNaN(Date.parse(s)) ? new Date(s).toLocaleString() : "—");
const fmtTime = (s) => (s && !isNaN(Date.parse(s)) ? new Date(s).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");
const fmtDur = (sec) => {
  if (sec == null) return "—";
  sec = Math.round(sec);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60), s = sec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const Card = ({ label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`ua-card-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="mb-2 text-[11px] uppercase tracking-widest text-[#A1A1AA]">{label}</div>
    <div className={`font-display text-3xl font-black ${accent || "text-white"}`}>{value}</div>
  </div>
);

const Online = ({ on }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${on ? "text-[#22c55e]" : "text-[#A1A1AA]"}`}>
    <Circle size={7} className={on ? "fill-[#22c55e] text-[#22c55e]" : "fill-[#52525b] text-[#52525b]"} /> {on ? "Online" : "Offline"}
  </span>
);

const RankList = ({ title, rows, unit = "" }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
    <div className="mb-3 text-sm font-bold text-white">{title}</div>
    {(!rows || rows.length === 0) ? <div className="text-xs text-[#A1A1AA]">No data yet.</div> : (
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 truncate text-white/80"><span className="text-[#A1A1AA]">{i + 1}.</span> {r.name || r.term}</span>
            <span className="font-mono text-xs text-[#00F0FF]">{r.count}{unit}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const PrivacyNote = () => (
  <div className="flex items-start gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs text-[#A1A1AA]" data-testid="ua-privacy-note">
    <Shield size={14} className="mt-0.5 shrink-0 text-[#A1A1AA]" />
    <span>Location is approximate and derived from network information. Analytics records only meaningful app events — never passwords, tokens, payment details or field contents. Detailed events are retained ~90 days.</span>
  </div>
);

// -------------------- Overview --------------------
function Overview({ onOpenUser, onOpenSession }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { setBusy(true); api.adminAnalytics().then(setD).catch(() => setD(null)).finally(() => setBusy(false)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PrivacyNote />
        <button onClick={load} disabled={busy} data-testid="ua-overview-refresh" className="ml-3 flex shrink-0 items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white disabled:opacity-50"><RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Refresh</button>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Card label="Total Users" value={d.total_users} />
        <Card label="Active Users" value={d.active_users} accent="text-[#22c55e]" />
        <Card label="Visits" value={d.total_visits} />
        <Card label="Unique Visitors" value={d.unique_visitors} />
        <Card label="Sessions" value={d.total_sessions} />
        <Card label="Avg Session" value={fmtDur(d.avg_session_seconds)} accent="text-[#00F0FF]" />
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Card label="New Today" value={d.new_today} />
        <Card label="New This Week" value={d.new_week} />
        <Card label="New This Month" value={d.new_month} />
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="ua-recent-logins">
        <div className="mb-3 text-sm font-bold text-white">Recent Logins</div>
        {(!d.recent_logins || d.recent_logins.length === 0) ? <div className="text-xs text-[#A1A1AA]">No logins recorded yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-widest text-[#A1A1AA]">
                <th className="py-2">User</th><th>Login</th><th>Location</th><th>Device</th><th>Browser</th><th>Duration</th><th>Status</th>
              </tr></thead>
              <tbody>
                {d.recent_logins.map((r, i) => (
                  <tr key={i} className="cursor-pointer border-t border-white/5 hover:bg-white/[0.03]" onClick={() => r.user_id && onOpenUser(r.user_id)} data-testid={`ua-login-row-${i}`}>
                    <td className="py-2 text-white/90">{r.email || r.name || "Anonymous"}</td>
                    <td className="text-[#A1A1AA]">{fmtDate(r.login_at)}</td>
                    <td className="text-[#A1A1AA]">{[r.city, r.region, r.country].filter(Boolean).join(", ") || "—"}</td>
                    <td className="text-[#A1A1AA]">{r.device || "—"}</td>
                    <td className="text-[#A1A1AA]">{r.browser || "—"}</td>
                    <td className="text-[#A1A1AA]">{fmtDur(r.session_seconds)}</td>
                    <td><Online on={r.online} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <RankList title="Top Pages" rows={d.top_pages} />
        <RankList title="Top Tools" rows={d.top_tools} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="ua-most-searched-fonts">
          <div className="mb-3 text-sm font-bold text-white">Most Searched Fonts</div>
          {(!d.most_searched_fonts || d.most_searched_fonts.length === 0) ? <div className="text-xs text-[#A1A1AA]">No font searches yet.</div> :
            d.most_searched_fonts.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-1 text-sm">
                <span className="text-white/80"><span className="text-[#A1A1AA]">{i + 1}.</span> {r.term}</span>
                <span className="font-mono text-xs text-[#00F0FF]">{r.count} searches</span>
              </div>
            ))}
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="ua-zero-result-fonts">
          <div className="mb-3 text-sm font-bold text-white">Zero-Result Searches</div>
          {(!d.zero_result_searches || d.zero_result_searches.length === 0) ? <div className="text-xs text-[#A1A1AA]">None — every search returned results.</div> :
            d.zero_result_searches.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-1 text-sm">
                <span className="text-white/80"><span className="text-[#A1A1AA]">{i + 1}.</span> {r.term}</span>
                <span className="font-mono text-xs text-[#f59e0b]">{r.count}×</span>
              </div>
            ))}
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="ua-recent-activity">
        <div className="mb-3 text-sm font-bold text-white">Recent Activity</div>
        {(!d.recent_activity || d.recent_activity.length === 0) ? <div className="text-xs text-[#A1A1AA]">No activity yet.</div> : (
          <div className="space-y-1.5">
            {d.recent_activity.map((e, i) => (
              <div key={i} className="flex items-center gap-3 border-t border-white/5 py-1.5 text-sm first:border-0" onClick={() => e.session_id && onOpenSession(e.session_id)} role="button">
                <span className="w-24 shrink-0 font-mono text-[11px] text-[#A1A1AA] sm:w-36">{fmtDate(e.created_at)}</span>
                <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-[#00F0FF]">{e.event_type}</span>
                <span className="truncate text-white/80">{e.action || e.target || e.page}</span>
                <span className="ml-auto hidden shrink-0 text-[11px] text-[#A1A1AA] sm:inline">{e.email || "anon"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------- User list --------------------
function UserList({ onOpenUser }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("last_active");
  const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [cf, setCf] = useState("");
  const [ct, setCt] = useState("");
  const [busy, setBusy] = useState(false);
  const limit = 25;

  const load = useCallback(() => {
    setBusy(true);
    api.adminUsers({ q, status, sort, direction: dir, page, limit, created_from: cf || undefined, created_to: ct || undefined })
      .then(setData).catch(() => setData({ users: [], total: 0 })).finally(() => setBusy(false));
  }, [q, status, sort, dir, page, cf, ct]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / limit)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <input data-testid="ua-user-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search user or email…"
            className="w-full rounded-lg border border-white/10 bg-white/[0.03] py-2 pl-9 pr-3 text-sm text-white outline-none focus:border-[#00F0FF]/50" />
        </div>
        <select data-testid="ua-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white">
          <option value="">All status</option><option value="admin">Admin</option><option value="pro">Pro</option><option value="free">Free</option>
        </select>
        <select data-testid="ua-sort" value={`${sort}:${dir}`} onChange={(e) => { const [s, d2] = e.target.value.split(":"); setSort(s); setDir(d2); }} className="rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white">
          <option value="last_active:desc">Last active ↓</option>
          <option value="created:desc">Newest</option>
          <option value="created:asc">Oldest</option>
          <option value="sessions:desc">Most sessions</option>
          <option value="visits:desc">Most visits</option>
          <option value="email:asc">Email A–Z</option>
        </select>
        <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">From <input type="date" data-testid="ua-created-from" value={cf} onChange={(e) => { setCf(e.target.value); setPage(1); }} className="rounded border border-white/10 bg-[#0A0A0A] px-2 py-1.5 text-white" /></label>
        <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">To <input type="date" data-testid="ua-created-to" value={ct} onChange={(e) => { setCt(e.target.value); setPage(1); }} className="rounded border border-white/10 bg-[#0A0A0A] px-2 py-1.5 text-white" /></label>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full min-w-[980px] text-sm" data-testid="ua-user-table">
          <thead><tr className="bg-white/[0.03] text-left text-[11px] uppercase tracking-widest text-[#A1A1AA]">
            <th className="px-4 py-3">User</th><th>Registered</th><th>Last active</th><th>Sessions</th><th>Visits</th><th>Last feature</th><th>Location</th><th>Status</th>
          </tr></thead>
          <tbody>
            {busy && !data ? <tr><td colSpan={8} className="px-4 py-10 text-center"><Loader2 className="mx-auto animate-spin text-[#00F0FF]" /></td></tr> :
              (data?.users || []).length === 0 ? <tr><td colSpan={8} className="px-4 py-10 text-center text-[#A1A1AA]" data-testid="ua-users-empty">No users found.</td></tr> :
                data.users.map((u) => (
                  <tr key={u.user_id} className="cursor-pointer border-t border-white/5 hover:bg-white/[0.03]" onClick={() => onOpenUser(u.user_id)} data-testid={`ua-user-row-${u.user_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-white/90">{u.name || u.email?.split("@")[0]}</div>
                      <div className="text-[11px] text-[#A1A1AA]">{u.email}{u.role === "admin" && <span className="ml-1 text-[#FFD400]">• admin</span>}</div>
                    </td>
                    <td className="text-[#A1A1AA]">{fmtDate(u.created_at)}</td>
                    <td className="text-[#A1A1AA]">{fmtDate(u.last_active)}</td>
                    <td className="text-white/80">{u.sessions}</td>
                    <td className="text-white/80">{u.visits}</td>
                    <td className="text-[#A1A1AA]">{u.last_feature || "—"}</td>
                    <td className="text-[#A1A1AA]">{u.location || "—"}</td>
                    <td><Online on={u.online} /></td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-[#A1A1AA]">
        <span>{data?.total || 0} users</span>
        <div className="flex items-center gap-2">
          <button data-testid="ua-page-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-white/15 p-1.5 disabled:opacity-40"><ChevronLeft size={16} /></button>
          <span>Page {page} / {totalPages}</span>
          <button data-testid="ua-page-next" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-white/15 p-1.5 disabled:opacity-40"><ChevronRight size={16} /></button>
        </div>
      </div>
    </div>
  );
}

// -------------------- User detail --------------------
function UserDetail({ uid, onBack, onOpenSession }) {
  const [d, setD] = useState(null);
  const [sessions, setSessions] = useState(null);
  useEffect(() => {
    api.adminUserDetail(uid).then(setD).catch(() => setD(null));
    api.adminUserSessions(uid).then((r) => setSessions(r.sessions || [])).catch(() => setSessions([]));
  }, [uid]);
  if (!d) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;
  const u = d.user;
  return (
    <div className="space-y-5" data-testid="ua-user-detail">
      <button onClick={onBack} data-testid="ua-detail-back" className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white"><ArrowLeft size={15} /> Back to users</button>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="font-display text-2xl font-black text-white">{u.name || u.email}</div>
            <div className="text-sm text-[#A1A1AA]">{u.email} {u.role === "admin" && <span className="text-[#FFD400]">• admin</span>}</div>
            <div className="mt-1 flex items-center gap-1 text-xs text-[#A1A1AA]"><MapPin size={12} /> {d.location || "Location unknown"}</div>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm sm:grid-cols-3">
            <div><div className="text-[10px] uppercase text-[#A1A1AA]">Joined</div>{fmtDate(u.created_at)}</div>
            <div><div className="text-[10px] uppercase text-[#A1A1AA]">Last login</div>{fmtDate(d.last_login)}</div>
            <div><div className="text-[10px] uppercase text-[#A1A1AA]">Last active</div>{fmtDate(d.last_active)}</div>
            <div><div className="text-[10px] uppercase text-[#A1A1AA]">Sessions</div>{d.sessions}</div>
            <div><div className="text-[10px] uppercase text-[#A1A1AA]">Visits</div>{d.visits}</div>
            <div><div className="text-[10px] uppercase text-[#A1A1AA]">Plan</div>{u.subscription_status || "free"}</div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <RankList title="Most Used Tools" rows={d.top_tools} />
        <RankList title="Most Visited Pages" rows={d.top_pages} />
        <RankList title="Font Searches" rows={d.font_searches?.map((f) => ({ name: f.term, count: f.count }))} />
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="ua-user-sessions">
        <div className="mb-3 text-sm font-bold text-white">Sessions</div>
        {sessions === null ? <Loader2 className="animate-spin text-[#00F0FF]" /> :
          sessions.length === 0 ? <div className="text-xs text-[#A1A1AA]">No sessions recorded.</div> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-widest text-[#A1A1AA]">
                  <th className="py-2">Started</th><th>Duration</th><th>Location</th><th>Device</th><th>Browser</th><th>OS</th><th>Events</th><th>Status</th><th></th>
                </tr></thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id} className="border-t border-white/5">
                      <td className="py-2 text-[#A1A1AA]">{fmtDate(s.started_at)}</td>
                      <td className="text-white/80">{fmtDur(s.duration_seconds)}</td>
                      <td className="text-[#A1A1AA]">{s.location || "—"}</td>
                      <td className="text-[#A1A1AA]">{s.device || "—"}</td>
                      <td className="text-[#A1A1AA]">{s.browser || "—"}</td>
                      <td className="text-[#A1A1AA]">{s.os || "—"}</td>
                      <td className="text-white/80">{s.event_count}</td>
                      <td>{s.status === "active" ? <Online on /> : <span className="text-[11px] text-[#A1A1AA]">ended</span>}</td>
                      <td><button data-testid={`ua-view-session-${s.id}`} onClick={() => onOpenSession(s.id)} className="rounded-lg border border-white/15 px-2.5 py-1 text-xs font-semibold text-[#00F0FF] hover:bg-white/5">View Session</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="mb-3 text-sm font-bold text-white">Recent Activity</div>
        {(d.recent_activity || []).length === 0 ? <div className="text-xs text-[#A1A1AA]">No activity.</div> :
          d.recent_activity.map((e, i) => (
            <div key={i} className="flex items-center gap-3 border-t border-white/5 py-1.5 text-sm first:border-0">
              <span className="w-24 shrink-0 font-mono text-[11px] text-[#A1A1AA] sm:w-36">{fmtDate(e.created_at)}</span>
              <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] uppercase text-[#00F0FF]">{e.event_type}</span>
              <span className="truncate text-white/80">{e.action || e.target || e.page}</span>
            </div>
          ))}
      </div>
    </div>
  );
}

// -------------------- Session timeline --------------------
const FILTERS = [
  ["all", "All"], ["page_views", "Page views"], ["clicks", "Clicks"], ["searches", "Searches"],
  ["uploads", "Uploads"], ["template_actions", "Template"], ["font_actions", "Font"],
  ["generate_actions", "Generate"], ["export_actions", "Export"], ["login_logout", "Login/Logout"], ["errors", "Errors"],
];

function SessionView({ sid, onBack }) {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.adminSessionDetail(sid, filter === "all" ? undefined : filter).then(setD).catch(() => setD(null)); }, [sid, filter]);
  const s = d?.session;
  return (
    <div className="space-y-4" data-testid="ua-session-view">
      <button onClick={onBack} data-testid="ua-session-back" className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white"><ArrowLeft size={15} /> Back</button>
      {!d ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00F0FF]" /></div> : (
        <>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="font-display text-lg font-bold text-white">{s.email || s.name || "Anonymous"}</div>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#A1A1AA]">
              <span className="font-mono">ID: {s.id.slice(0, 8)}</span>
              <span className="flex items-center gap-1"><Clock size={12} /> {fmtDur(s.duration_seconds)}</span>
              <span className="flex items-center gap-1"><MapPin size={12} /> {s.location || "—"}</span>
              <span className="flex items-center gap-1"><Monitor size={12} /> {[s.device, s.browser, s.os].filter(Boolean).join(" · ") || "—"}</span>
              <span>{s.event_count} events</span>
              <span>{s.status === "active" ? "Active" : "Ended"}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map(([v, l]) => (
              <button key={v} data-testid={`ua-filter-${v}`} onClick={() => setFilter(v)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${filter === v ? "bg-[#00F0FF] text-black" : "border border-white/15 text-[#A1A1AA] hover:text-white"}`}>{l}</button>
            ))}
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="ua-timeline">
            {(d.events || []).length === 0 ? <div className="text-xs text-[#A1A1AA]">No events for this filter.</div> : (
              <ol className="relative ml-3 border-l border-white/10">
                {d.events.map((e, i) => (
                  <li key={i} className="mb-4 ml-5" data-testid={`ua-timeline-event-${i}`}>
                    <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full border-2 border-[#0A0A0A] bg-[#00F0FF]" />
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-[#A1A1AA]">{fmtTime(e.created_at)}</span>
                      <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] uppercase text-[#00F0FF]">{e.event_type}</span>
                    </div>
                    <div className="mt-0.5 text-sm text-white/90">{e.action || e.target || e.page || e.event_type}</div>
                    {(e.feature || e.page || e.target) && (
                      <div className="text-[11px] text-[#A1A1AA]">{[e.page, e.feature, e.target].filter(Boolean).join(" · ")}</div>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// -------------------- Activity search --------------------
function ActivitySearch() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [et, setEt] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setBusy(true);
    api.adminAnalyticsActivity({ q: q || undefined, event_type: et || undefined, user_email: email || undefined, limit: 60 })
      .then((r) => setRows(r.events || [])).catch(() => setRows([])).finally(() => setBusy(false));
  }, [q, et, email]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <input data-testid="ua-activity-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search activity, font, page, feature…" className="w-full rounded-lg border border-white/10 bg-white/[0.03] py-2 pl-9 pr-3 text-sm text-white outline-none focus:border-[#00F0FF]/50" />
        </div>
        <input data-testid="ua-activity-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Filter by email" className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF]/50" />
        <select data-testid="ua-activity-type" value={et} onChange={(e) => setEt(e.target.value)} className="rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white">
          {FILTERS.map(([v, l]) => <option key={v} value={v === "all" ? "" : v}>{l}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full min-w-[820px] text-sm">
          <thead><tr className="bg-white/[0.03] text-left text-[11px] uppercase tracking-widest text-[#A1A1AA]"><th className="px-4 py-3">Time</th><th>User</th><th>Type</th><th>Action</th><th>Target</th><th>Page</th></tr></thead>
          <tbody>
            {busy && !rows ? <tr><td colSpan={6} className="px-4 py-10 text-center"><Loader2 className="mx-auto animate-spin text-[#00F0FF]" /></td></tr> :
              (rows || []).length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-center text-[#A1A1AA]" data-testid="ua-activity-empty">No matching activity.</td></tr> :
                rows.map((e, i) => (
                  <tr key={i} className="border-t border-white/5" data-testid={`ua-activity-row-${i}`}>
                    <td className="px-4 py-2 font-mono text-[11px] text-[#A1A1AA]">{fmtDate(e.created_at)}</td>
                    <td className="text-white/80">{e.email || "anon"}</td>
                    <td><span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] uppercase text-[#00F0FF]">{e.event_type}</span></td>
                    <td className="text-white/80">{e.action || "—"}</td>
                    <td className="text-[#A1A1AA]">{e.target || "—"}</td>
                    <td className="text-[#A1A1AA]">{e.page || "—"}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------- Font search analytics --------------------
function FontSearchAnalytics() {
  const [d, setD] = useState(null);
  const [range, setRange] = useState("all");
  const [sort, setSort] = useState("most");
  useEffect(() => { api.adminFontSearches({ range, sort }).then(setD).catch(() => setD(null)); }, [range, sort]);
  if (!d) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;
  return (
    <div className="space-y-5" data-testid="ua-font-analytics">
      <div className="flex flex-wrap gap-2">
        <select data-testid="ua-font-range" value={range} onChange={(e) => setRange(e.target.value)} className="rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white">
          <option value="today">Today</option><option value="7d">7 days</option><option value="30d">30 days</option><option value="90d">90 days</option><option value="all">All time</option>
        </select>
        <select data-testid="ua-font-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white">
          <option value="most">Most searched</option><option value="least">Least searched</option><option value="recent">Most recent</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card label="Total Searches" value={d.total_searches} />
        <Card label="Unique Terms" value={d.unique_terms} />
        <Card label="Users Searching" value={d.searchers} />
        <Card label="Zero-Result" value={d.zero_result_total} accent="text-[#f59e0b]" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="mb-3 text-sm font-bold text-white">Most Searched Fonts</div>
          {(d.most_searched || []).length === 0 ? <div className="text-xs text-[#A1A1AA]">No searches.</div> :
            d.most_searched.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-1 text-sm">
                <span className="text-white/80"><span className="text-[#A1A1AA]">{i + 1}.</span> {r.term}</span>
                <span className="font-mono text-xs text-[#00F0FF]">{r.count != null ? `${r.count} searches` : fmtDate(r.at)}</span>
              </div>
            ))}
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="mb-3 text-sm font-bold text-white">Zero-Result Searches</div>
          {(d.zero_result_searches || []).length === 0 ? <div className="text-xs text-[#A1A1AA]">None.</div> :
            d.zero_result_searches.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-1 text-sm">
                <span className="text-white/80"><span className="text-[#A1A1AA]">{i + 1}.</span> {r.term}</span>
                <span className="font-mono text-xs text-[#f59e0b]">{r.count}×</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// -------------------- Shell --------------------
const SUBTABS = [["overview", "Overview", LayoutDashboard], ["users", "User List", Users], ["activity", "Activity", Activity], ["fonts", "Font Searches", Type]];

export default function UserAnalytics() {
  const [view, setView] = useState({ tab: "overview" }); // {tab} | {user} | {session}
  const openUser = (uid) => setView({ user: uid });
  const openSession = (sid) => setView({ session: sid, from: view });

  if (view.user) return <UserDetail uid={view.user} onBack={() => setView({ tab: "users" })} onOpenSession={(sid) => setView({ session: sid, from: { user: view.user } })} />;
  if (view.session) return <SessionView sid={view.session} onBack={() => setView(view.from || { tab: "overview" })} />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-1.5" data-testid="ua-subnav">
        {SUBTABS.map(([v, l, Icon]) => (
          <button key={v} data-testid={`ua-subtab-${v}`} onClick={() => setView({ tab: v })}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${view.tab === v ? "bg-[#00F0FF] text-black" : "border border-white/15 text-[#A1A1AA] hover:text-white"}`}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>
      {view.tab === "overview" && <Overview onOpenUser={openUser} onOpenSession={openSession} />}
      {view.tab === "users" && <UserList onOpenUser={openUser} />}
      {view.tab === "activity" && <ActivitySearch />}
      {view.tab === "fonts" && <FontSearchAnalytics />}
    </div>
  );
}
